import React, { useCallback, useEffect, useRef } from "react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { resolveApiUrl } from "../../pathways/learnerApi";
import { watchedEnough } from "../../pathways/learnerModel";
import { embedProvider, withPlayerApi } from "../../lib/videoUrls";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

const YOUTUBE_API_SRC = "https://www.youtube.com/iframe_api";
const VIMEO_ORIGIN = "https://player.vimeo.com";
const POLL_MS = 1000;
/** How long an embed may stay silent before its progress is treated as unobservable. */
export const TRACKING_TIMEOUT_MS = 15000;

let youTubeApi = null;

/**
 * The YouTube IFrame Player API, loaded once per page: the first caller injects the script,
 * every caller shares the promise. The API announces itself through the global
 * `onYouTubeIframeAPIReady`; a handler another script installed first is chained, not
 * replaced. A failed load is forgotten, so a later video tries again.
 *
 * @returns {Promise<any>} `window.YT`
 */
export function loadYouTubeApi() {
  if (window.YT && typeof window.YT.Player === "function") return Promise.resolve(window.YT);
  if (youTubeApi) return youTubeApi;
  youTubeApi = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === "function") previous();
      resolve(window.YT);
    };
    const script = document.createElement("script");
    script.src = YOUTUBE_API_SRC;
    script.async = true;
    script.onerror = () => {
      youTubeApi = null;
      reject(new Error("YouTube IFrame API failed to load"));
    };
    document.head.appendChild(script);
  });
  return youTubeApi;
}

/**
 * The watchdog every embed shares: `untracked()` fires unless the player proves it is
 * talking to this page (`reported()`) within TRACKING_TIMEOUT_MS of mounting. An embed
 * that never reports (the API script blocked, a player that never answers) must not leave a
 * mandatory step with no way to complete it.
 */
function useReportWatchdog(tracking, onUntracked) {
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!tracking) return undefined;
    const timer = setTimeout(() => { if (!reportedRef.current) onUntracked(); }, TRACKING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [tracking, onUntracked]);
  return useCallback(() => { reportedRef.current = true; }, []);
}

/**
 * A YouTube embed whose progress is read through the IFrame Player API: while the player
 * reports PLAYING, its position is polled once a second; ENDED is checked too. A failed API
 * load or a player error is reported as untracked at once; silence, after the watchdog.
 */
function YouTubeFrame({ src, title, tracking, onProgress, onUntracked }) {
  const frameRef = useRef(null);
  const reported = useReportWatchdog(tracking, onUntracked);

  useEffect(() => {
    if (!tracking) return undefined;
    let cancelled = false;
    let timer = null;
    let player = null;
    const read = () => {
      if (!player || typeof player.getCurrentTime !== "function") return;
      onProgress(player.getCurrentTime(), player.getDuration());
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    loadYouTubeApi().then((YT) => {
      if (cancelled || !frameRef.current) return;
      player = new YT.Player(frameRef.current, {
        events: {
          onReady: () => reported(),
          onError: () => { if (!cancelled) onUntracked(); },
          onStateChange: (event) => {
            reported();
            const states = YT.PlayerState || {};
            if (event.data === states.PLAYING) {
              stop();
              timer = setInterval(read, POLL_MS);
            } else {
              stop();
              read();
            }
          },
        },
      });
    }).catch(() => {
      // The video still plays without the API; only the automatic completion is lost,
      // so the learner is offered the manual one.
      if (!cancelled) onUntracked();
    });
    return () => { cancelled = true; stop(); };
  }, [tracking, onProgress, onUntracked, reported]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      data-testid={IDS.stepVideo}
      className="aspect-video w-full rounded-2xl bg-eh-ink"
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
    />
  );
}

/**
 * A Vimeo embed whose progress arrives through Vimeo's postMessage API: once the player
 * says "ready", this page subscribes to its time events (`timeupdate`, and the older
 * player's `playProgress`), and reads `percent` (0..1) or `seconds / duration`. Only
 * messages from THIS frame, from Vimeo's origin, are read, and any of them proves the
 * player is talking; silence is reported as untracked after the watchdog.
 */
function VimeoFrame({ src, title, tracking, onProgress, onUntracked }) {
  const frameRef = useRef(null);
  const reported = useReportWatchdog(tracking, onUntracked);

  const subscribe = useCallback(() => {
    const target = frameRef.current?.contentWindow;
    if (!target) return;
    for (const value of ["timeupdate", "playProgress", "ended", "finish", "error"]) {
      target.postMessage(JSON.stringify({ method: "addEventListener", value }), VIMEO_ORIGIN);
    }
  }, []);

  useEffect(() => {
    if (!tracking) return undefined;
    const onMessage = (event) => {
      if (event.origin !== VIMEO_ORIGIN) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      let message = event.data;
      if (typeof message === "string") {
        try { message = JSON.parse(message); } catch { return; }
      }
      if (!message || typeof message !== "object") return;
      reported();
      if (message.event === "ready") { subscribe(); return; }
      // A playback error (a codec the browser lacks, a private video) means progress will
      // never arrive: offer the manual completion at once, as a YouTube player error does.
      if (message.event === "error") { onUntracked(); return; }
      if (message.event === "ended" || message.event === "finish") { onProgress(1, 1); return; }
      if (message.event === "timeupdate" || message.event === "playProgress") {
        const data = message.data || {};
        if (typeof data.percent === "number") onProgress(data.percent, 1);
        else onProgress(data.seconds, data.duration);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [tracking, onProgress, onUntracked, subscribe, reported]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      onLoad={tracking ? subscribe : undefined}
      data-testid={IDS.stepVideo}
      className="aspect-video w-full rounded-2xl bg-eh-ink"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
    />
  );
}

/**
 * A video step's player (contract §5.4). It completes the step when it observes >= 90 %
 * watched, which is client-attested: the server cannot see playback.
 *
 *   upload   an HTML5 <video> over the gated file URL; progress from `timeupdate`
 *   link     a YouTube or Vimeo embed, with its player API switched on at render time
 *            (lib/videoUrls.withPlayerApi)
 *
 * EXACTLY ONCE. `onWatched` fires the first time the mark is reached, never again for
 * this mounted player, however many progress events follow. The parent keys the player by
 * step, so another step gets its own. `tracking` false (the step is already done, or this
 * is the practitioner's preview) turns every listener off.
 *
 * NEVER A DEAD END. When progress cannot be observed, `onUntracked` fires (at most once)
 * and the parent offers a manual "Mark as done" instead:
 *   - a YouTube API that fails to load, or a player error;
 *   - an embed that never reports within TRACKING_TIMEOUT_MS;
 *   - an uploaded file whose duration is not finite (a stream, broken metadata);
 *   - a link that is not a playable embed at all.
 *
 * @param {object} props
 * @param {{ source: "upload", url: string, content_type?: string } | { source: "link", url: string }} props.video
 * @param {string} props.title          the step title, the frame's accessible name
 * @param {boolean} props.tracking
 * @param {() => void} props.onWatched
 * @param {() => void} props.onUntracked
 */
export default function StepVideo({ video, title, tracking, onWatched, onUntracked }) {
  const t = useT("public");
  const firedRef = useRef(false);
  const untrackedRef = useRef(false);

  const onProgress = useCallback((position, duration) => {
    if (!tracking || firedRef.current) return;
    if (!watchedEnough(position, duration)) return;
    firedRef.current = true;
    onWatched();
  }, [tracking, onWatched]);

  const untracked = useCallback(() => {
    if (!tracking || untrackedRef.current || firedRef.current) return;
    untrackedRef.current = true;
    onUntracked();
  }, [tracking, onUntracked]);

  const provider = video.source === "link" ? embedProvider(video.url) : "";
  const unplayable = video.source === "link" && !provider;

  useEffect(() => { if (unplayable) untracked(); }, [unplayable, untracked]);

  if (video.source === "upload") {
    // A duration that is not a finite number of seconds can never reach 90 %.
    const checkDuration = (e) => { if (!Number.isFinite(e.currentTarget.duration)) untracked(); };
    return (
      // No <track>: the contract stores no caption file for a lesson video.
      <video
        controls
        preload="metadata"
        src={resolveApiUrl(video.url)}
        title={title}
        data-testid={IDS.stepVideo}
        onLoadedMetadata={checkDuration}
        onDurationChange={checkDuration}
        onTimeUpdate={(e) => {
          if (!Number.isFinite(e.currentTarget.duration)) { untracked(); return; }
          onProgress(e.currentTarget.currentTime, e.currentTarget.duration);
        }}
        onEnded={(e) => onProgress(e.currentTarget.duration, e.currentTarget.duration)}
        className="aspect-video w-full rounded-2xl bg-eh-ink"
      />
    );
  }

  const src = withPlayerApi(video.url, window.location.origin);
  if (provider === "youtube") {
    return <YouTubeFrame src={src} title={title} tracking={tracking} onProgress={onProgress} onUntracked={untracked} />;
  }
  if (provider === "vimeo") {
    return <VimeoFrame src={src} title={title} tracking={tracking} onProgress={onProgress} onUntracked={untracked} />;
  }
  return <p className="rounded-2xl bg-eh-cream-deep px-4 py-6 text-sm text-eh-muted">{t("pathways.step.videoUnavailable")}</p>;
}
